import { useState, useEffect } from 'react'
import styles from './dishesCardToOrder.module.css'
import arrowPrev from '../../assets/icons/arrowLeft.svg'
import arrowNext from '../../assets/icons/arrowRight.svg'
import noImage from '../../assets/img/no-image.jpg'
import { Slide } from 'react-slideshow-image'
import CustomButton from '../UI/CustomButton/CustomButton'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { updateOrderDetailQuantity, addOrderDetail } from '../../redux/reducers/claims/claimsSlice'
import { OrderProductType } from '../../redux/actions/types'

interface DishesCardToOrderProps {
  name: string
  images: string[]
  price: number
  description: string
  weight: number | string
  measurement: string
  id: number
  currentOrder: OrderProductType | undefined
  quantity: number
  category: string
  categoryId: number
  categoryName: string
}

const formatMeasurement = (weight: number | string, measurement: string): string => {
  const measurementMap: { [key: string]: string } = {
    миллилитров: 'мл',
    миллилитр: 'мл',
    литров: 'л',
    литр: 'л',
    граммов: 'г',
    грамм: 'г',
    килограммов: 'кг',
    килограмм: 'кг',
    штук: 'шт',
    штука: 'шт',
    порций: 'порц',
    порция: 'порц',
    упаковка: 'уп',
    упаковки: 'уп',
  }

  const shortMeasurement = measurementMap[measurement.toLowerCase()] || measurement
  return `${weight} ${shortMeasurement}`
}

const DishesCardToOrder: React.FC<DishesCardToOrderProps> = ({
  name,
  images,
  price,
  description,
  weight,
  measurement,
  currentOrder,
  id,
  categoryId,
  categoryName,
  quantity,
  category,
}) => {
  const dispatch = useAppDispatch()
  const { currentClaim } = useAppSelector((state) => state.claims)

  const [inputValue, setInputValue] = useState<string>(quantity.toString())

  useEffect(() => {
    setInputValue(quantity.toString())
  }, [quantity])

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const properties = {
    canSwipe: false,
    duration: 5000,
    autoplay: false,
    transitionDuration: 500,
    arrows: false,
    infinite: true,
    easing: 'ease',
    indicators: () => <div className='sliderIndicator'></div>,
    prevArrow: (
      <div className={styles.arrowPrevBlock} onClick={handleArrowClick}>
        <img className={styles.arrowPrev} src={arrowPrev} alt='arrowPrev' />
      </div>
    ),
    nextArrow: (
      <div className={styles.arrowNextBlock} onClick={handleArrowClick}>
        <img className={styles.arrowNext} src={arrowNext} alt='arrowNext' />
      </div>
    ),
  }

  const searchParams = new URLSearchParams(location.search)
  const claimId = searchParams.get('id')

  const handleIncrease = () => {
    if (currentOrder) {
      dispatch(
        updateOrderDetailQuantity({
          claimId: Number(claimId),
          orderDetailId: currentOrder.productId ?? 0,
          newQuantity: currentOrder.quantity + 1,
        }),
      )
    }
  }

  const handleDecrease = () => {
    if (currentOrder && currentOrder.quantity > 1) {
      dispatch(
        updateOrderDetailQuantity({
          claimId: Number(claimId),
          orderDetailId: currentOrder.productId ?? 0,
          newQuantity: currentOrder.quantity - 1,
        }),
      )
    }
  }

  const onCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)

    const val = Number(value)

    if (!isNaN(val) && val >= 1) {
      dispatch(
        updateOrderDetailQuantity({
          claimId: Number(claimId),
          orderDetailId: currentOrder?.productId ?? 0,
          newQuantity: val,
        }),
      )
    }
  }

  const handleAddToOrder = () => {
    const newOrderDetail: OrderProductType = {
      productId: id,
      name: name,
      quantity: 1,
      price: price,
      category: {
        id: categoryId,
        name: categoryName,
      },
      images: images,
    }

    dispatch(
      addOrderDetail({
        claimId: Number(claimId),
        orderDetail: newOrderDetail,
      }),
    )
  }

  return (
    <div className={styles.dishesCard}>
      <div className={styles.left}>
        <div className={styles.slider}>
          {images.length !== 0 ? (
            <Slide {...properties} cssClass='sliderContainer'>
              {images.map((slideImage, index) => (
                <div
                  key={index}
                  className={styles.slideImage}
                  style={{
                    backgroundImage: `url('${process.env.REACT_APP_BASE_URL_IMAGES}/${slideImage}')`,
                    zIndex: 0,
                  }}
                ></div>
              ))}
            </Slide>
          ) : (
            <div className={styles.slideImage}>
              <img className={styles.slideImage} src={noImage} alt='noImage' />
            </div>
          )}
        </div>
      </div>
      <div className={styles.center}>
        <div className={styles.top}>
          <h3 className={styles.name}>{name}</h3>
          <h5 className={styles.description}>{description}</h5>
        </div>
      </div>

      <div className={styles.plusButtonBlock}>
        <div className={styles.price}>
          <h3>{price} руб</h3>
          <h5 className={styles.weight}>{formatMeasurement(weight, measurement)}</h5>
        </div>

        {currentOrder ? (
          <div className={styles.countBlock}>
            <CustomButton className={styles.countButton} title='-' onClick={handleDecrease} />
            <input
              min={1}
              value={inputValue}
              onChange={onCountChange}
              className={styles.countInput}
            />
            <CustomButton className={styles.countButton} title='+' onClick={handleIncrease} />
          </div>
        ) : (
          <>
            <CustomButton title='Добавить' className={styles.addBtn} onClick={handleAddToOrder} />
          </>
        )}
      </div>
    </div>
  )
}

export default DishesCardToOrder
